import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Typography,
} from '@mui/material';
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';
import { UserDataContext } from '../../firebase/UserDataContext';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CommonDataContext } from '../../firebase/CommonDataContext';

const LiabilityFormText = ({ specificDescription, liabilityFormLink }) => {
    return (
        <>
            <Typography variant="body1"><b>Terms and Conditions of Participation.</b></Typography>
            <br /><br />
            <Typography variant="body1">
                This Competitor Release of Liability Agreement ("Agreement") is a legal agreement between you (the "Competitor") and the Aerospace Maintenance Council ("Organizer") regarding your participation in the Aerospace Maintenance Competition taking place April 9-11, 2024 in Chicago ("Event"). By clicking “I Agree,” below, you acknowledge that you have read, understood, and agree to be bound by the terms and conditions of this Agreement. Please read the following terms carefully. A print version of the form is available <a rel="noopener noreferrer" target="_blank" href={liabilityFormLink}>here.</a>
            </Typography>
            <br /><br />
            <Typography variant="body1"><b>Participation in the Event.</b> By agreeing to this Agreement, you acknowledge that participating in the Event involves certain risks and dangers, including the risk of personal injury, death, or property damage. You voluntarily assume all risks associated with your participation in the Event.</Typography>
            <br /><br />
            <Typography variant="body1"><b>Release of Liability.</b> In consideration of being allowed to participate in the Event, you hereby release, waive, discharge, and covenant not to sue the Organizer, Informa Markets, the McCormick Place Convention Center, the event sponsors, and all those organizations’ affiliates, and their respective officers, directors, employees, agents, and representatives from any and all liability, claims, demands, actions, and causes of action whatsoever, arising out of or related to any loss, damage, or injury, including death, that may be sustained by you during or as a result of your participation in the Event.</Typography>
            <br /><br />
            <Typography variant="body1"><b>Assumption of Risk.</b> You acknowledge and understand that participating in the Event may expose you to risks and dangers, both known and unknown. You acknowledge that such risks cannot be eliminated entirely and that you have chosen to participate in the Event voluntarily despite the risks.</Typography>
            <br /><br />
            <Typography variant="body1"><b>Indemnification.</b> You agree to indemnify and hold the Organizer harmless from any and all claims, actions, suits, procedures, costs, expenses, damages, and liabilities, including attorney's fees, arising out of or in connection with your participation in the Event.</Typography>
            <br /><br />
            <Typography variant="body1"><b>Governing Law.</b> This Agreement shall be governed by and construed in accordance with the laws of Oklahoma.</Typography>
            <br /><br />
            <Typography variant="body1"><b>Insurance.</b> You understand that each participant in the Event is mandated to have an active and enforceable hospital and medical insurance policy covering himself or herself during the duration of the Event and hereby certify that you meet that requirement prior to participation. You also certify that you are not presently under any physician’s care for any condition, whether disclosed or undisclosed, that would preclude your ability to actively participate in the Event.</Typography>
            <br /><br />
            <Typography variant="body1">
                By clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by the terms and conditions of this Agreement, acknowledge the Organizer <a href="https://www.aerospacecompetition.com/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy</a> and <a href="https://www.aerospacecompetition.com/media-policy.html" target="_blank" rel="noopener noreferrer">Media</a> policies, and that you waive any right to bring a legal action or assert a claim against the Organizer for any reason whatsoever.
            </Typography>
            <br /><br />
        </>
    );
};

function LiabilityFormDialog() {
    const { defaultSelectedCompetition, user } = useContext(UserDataContext);
    const { liabilityMap: globalLiabilityMap } = useContext(CommonDataContext);
    const { globalSettings: { competitions } } = useContext(GlobalSettingsContext);
    const { firebase } = useContext(FirebaseNewContext);

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);
    const [hasAgreed, setHasAgreed] = useState(false);
    const [mustConsent, setMustConsent] = useState(false);
    const [confirmSelf, setConfirmSelf] = useState(false);
    const [confirmSelfMinor, setConfirmSelfMinor] = useState(false);
    const [formLocation, setFormLocation] = useState('');

    useEffect(() => {
        const run = async () => {
            if (hasAgreed) return;
            if (!user?.loggedIn || !globalLiabilityMap || Object.keys(globalLiabilityMap).length === 0) return;

            const signatoriesResult = globalLiabilityMap[defaultSelectedCompetition]?.signatories || {};
            const formResult = await firebase.storageGetLiabilityForm('LiabilityForm.pdf', defaultSelectedCompetition);

            setFormLocation(formResult);
            if (!(user.uid in signatoriesResult) && formResult) {
                setMustConsent(true);
                setOpen(true);
            }
        };

        if (defaultSelectedCompetition && user) {
            run().catch(console.error);
        }
    }, [defaultSelectedCompetition, user, globalLiabilityMap, firebase, hasAgreed]);

    // Block backdrop click & ESC from closing
    const handleDialogClose = (event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
    };

    const submit = useCallback(() => {
        const didntConfirmSelf = !confirmSelf;
        const didntConfirmSelfMinor = !confirmSelfMinor;

        if (didntConfirmSelf && didntConfirmSelfMinor) {
            alert('You must check a box to proceed');
            return;
        }
        firebase.insertLiabilityForm(defaultSelectedCompetition, confirmSelf, confirmSelfMinor).then(() => {
            setHasAgreed(true);
            setOpen(false);
        });
    }, [confirmSelf, confirmSelfMinor, defaultSelectedCompetition, firebase]);

    if (!mustConsent) return null;

    const details = globalLiabilityMap[defaultSelectedCompetition]?.details || '';
    const canAgree = confirmSelf || confirmSelfMinor;

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            fullScreen={isSmall}
            fullWidth
            maxWidth="md"
            disableEscapeKeyDown
            scroll="paper"
            PaperProps={{
                sx: {
                    height: isSmall ? '100dvh' : '90vh',
                    display: 'flex',
                },
            }}
        >
            <DialogTitle>Liability Forms</DialogTitle>

            <DialogContent
                dividers
                sx={{ overflowY: 'auto' }}
            >
                <DialogContentText component="div">
                    <LiabilityFormText
                        specificDescription={details}
                        liabilityFormLink={formLocation}
                    />
                </DialogContentText>

                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmSelf}
                                onChange={(e) => setConfirmSelf(e.target.checked)}
                            />
                        }
                        label="I Agree"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmSelfMinor}
                                onChange={(e) => setConfirmSelfMinor(e.target.checked)}
                            />
                        }
                        label="I am a minor and will bring a guardian-signed copy of the agreement to the Event"
                    />
                </FormGroup>
            </DialogContent>

            <DialogActions
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: 'background.paper',
                    borderTop: 1,
                    borderColor: 'divider',
                    zIndex: 1,
                    py: 1,
                }}
            >
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={!canAgree}
                >
                    I Agree
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LiabilityFormDialog;
